// src/services/azureReferenceImagesService.js
// Azure Table Storage Service für Reference Images

const AZURE_CONFIG = {
  storageAccount: "mfprojects",
  tableName: "referenceImages",
  sasToken:
    "sv=2024-11-04&ss=t&srt=sco&sp=rwdlacu&se=2035-07-04T17:08:25Z&st=2025-07-04T09:08:25Z&spr=https&sig=X7s%2BFIyYcKvRzSxRNAp5BUJEe2YUcg8nojkn9fIfcTY%3D",
};

// Validierung
const validateConfig = () => {
  if (!AZURE_CONFIG.storageAccount) {
    throw new Error("Azure Storage Account nicht konfiguriert");
  }
  if (!AZURE_CONFIG.tableName) {
    throw new Error("Table Name nicht konfiguriert");
  }
  if (!AZURE_CONFIG.sasToken) {
    throw new Error("SAS Token für Table Storage nicht konfiguriert");
  }
};

// Alle Reference Images laden
export const getAllReferenceImages = async () => {
  try {
    console.log("Lade alle Reference Images...");

    validateConfig();

    let allImages = [];
    let continuationToken = null;

    do {
      let url = `https://${AZURE_CONFIG.storageAccount}.table.core.windows.net/${AZURE_CONFIG.tableName}?${AZURE_CONFIG.sasToken}`;

      if (continuationToken) {
        if (continuationToken.nextPartitionKey) {
          url += `&NextPartitionKey=${encodeURIComponent(continuationToken.nextPartitionKey)}`;
        }
        if (continuationToken.nextRowKey) {
          url += `&NextRowKey=${encodeURIComponent(continuationToken.nextRowKey)}`;
        }
      }

      const response = await fetch(url, {
        headers: { Accept: "application/json;odata=nometadata" },
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Table Load Error:", errorText);

        if (response.status === 404) {
          throw new Error(`Table '${AZURE_CONFIG.tableName}' nicht gefunden.`);
        } else if (response.status === 403) {
          throw new Error("Zugriff verweigert. SAS Token ist ungültig.");
        }

        throw new Error(
          `Laden fehlgeschlagen (${response.status}): ${errorText}`
        );
      }

      const data = await response.json();

      const images = data.value.map((item) => ({
        partitionKey: item.PartitionKey,
        rowKey: item.RowKey,
        createdAt: item.CreatedAt,
        fileName: item.FileName,
        fileSize: item.FileSize,
        location: item.Location || "",
        name: item.Name || "",
        office: item.Office || "",
        oneDrivePath: item.OneDrivePath || "",
        originalUrl: item.OriginalUrl || "",
        sharePointUrl: item.SharePointUrl || "",
        sourceReuse: item.SourceReuse || "",
        sourceWebsite: item.SourceWebsite || "",
        webSourceUrl: item.WebSourceUrl || "",
        tags: item.Tags || "",
        projectCodes: item.ProjectCodes || "",
        imageProjectCode: item.ImageProjectCode || "",
        imageDescription: item.ImageDescription || "",
        nextcloudPath: item.NextcloudPath || "",
        uploadedAt: item.UploadedAt,
        uploadedBy: item.UploadedBy || "",
      }));

      allImages = [...allImages, ...images];

      // Check for continuation tokens
      const nextPartitionKey = response.headers.get("x-ms-continuation-NextPartitionKey");
      const nextRowKey = response.headers.get("x-ms-continuation-NextRowKey");

      if (nextPartitionKey || nextRowKey) {
        continuationToken = { nextPartitionKey, nextRowKey };
      } else {
        continuationToken = null;
      }
    } while (continuationToken);

    console.log(`${allImages.length} Reference Images geladen`);
    return allImages;
  } catch (error) {
    console.error("Load Reference Images Error:", error);
    return [];
  }
};

// Reference Images nach Office gruppieren
export const getReferenceImagesByOffice = async () => {
  const allImages = await getAllReferenceImages();

  // Gruppiere nach Office + Name (Projekt)
  const projectsMap = new Map();

  allImages.forEach((image) => {
    const key = `${image.office}|||${image.name}`;

    if (!projectsMap.has(key)) {
      projectsMap.set(key, {
        office: image.office,
        name: image.name,
        location: image.location,
        images: [],
        tags: new Set(),
        projectCodes: new Set(),
      });
    }

    const project = projectsMap.get(key);
    project.images.push(image);

    // Parse and collect tags (semicolon-delimited)
    if (image.tags) {
      const tagList = image.tags.split(';').map(t => t.trim()).filter(t => t);
      tagList.forEach(tag => project.tags.add(tag));
    }

    // Parse and collect project codes (semicolon-delimited)
    if (image.projectCodes) {
      const codeList = image.projectCodes.split(';').map(c => c.trim()).filter(c => c);
      codeList.forEach(code => project.projectCodes.add(code));
    }
  });

  // Convert to array and sort images by createdAt
  const projects = Array.from(projectsMap.values()).map((project) => ({
    ...project,
    tags: Array.from(project.tags),
    projectCodes: Array.from(project.projectCodes),
    images: project.images.sort((a, b) =>
      new Date(a.createdAt) - new Date(b.createdAt)
    ),
    firstImage: project.images.sort((a, b) =>
      new Date(a.createdAt) - new Date(b.createdAt)
    )[0],
  }));

  // Sort projects by office name
  return projects.sort((a, b) => a.office.localeCompare(b.office));
};

// Search/Filter Reference Images
export const searchReferenceImages = async (searchTerm) => {
  const allImages = await getAllReferenceImages();

  if (!searchTerm || searchTerm.trim() === '') {
    return allImages;
  }

  const term = searchTerm.toLowerCase();

  return allImages.filter((image) => {
    return (
      (image.office && image.office.toLowerCase().includes(term)) ||
      (image.name && image.name.toLowerCase().includes(term)) ||
      (image.location && image.location.toLowerCase().includes(term)) ||
      (image.tags && image.tags.toLowerCase().includes(term)) ||
      (image.projectCodes && image.projectCodes.toLowerCase().includes(term))
    );
  });
};

// Get unique values for filters
export const getFilterOptions = async () => {
  const allImages = await getAllReferenceImages();

  const offices = new Set();
  const names = new Set();
  const locations = new Set();
  const tags = new Set();
  const projectCodes = new Set();

  allImages.forEach((image) => {
    if (image.office) offices.add(image.office);
    if (image.name) names.add(image.name);
    if (image.location) locations.add(image.location);
    if (image.tags) {
      image.tags.split(';').map(t => t.trim()).filter(t => t).forEach(tag => tags.add(tag));
    }
    if (image.projectCodes) {
      image.projectCodes.split(';').map(c => c.trim()).filter(c => c).forEach(code => projectCodes.add(code));
    }
  });

  return {
    offices: Array.from(offices).sort(),
    names: Array.from(names).sort(),
    locations: Array.from(locations).sort(),
    tags: Array.from(tags).sort(),
    projectCodes: Array.from(projectCodes).sort(),
  };
};

// Update ProjectCodes for an image (project-level codes)
export const updateProjectCodes = async (partitionKey, rowKey, projectCodes) => {
  try {
    validateConfig();

    const url = `https://${AZURE_CONFIG.storageAccount}.table.core.windows.net/${AZURE_CONFIG.tableName}(PartitionKey='${encodeURIComponent(partitionKey)}',RowKey='${encodeURIComponent(rowKey)}')?${AZURE_CONFIG.sasToken}`;

    const response = await fetch(url, {
      method: "MERGE",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json;odata=nometadata",
        "If-Match": "*",
      },
      body: JSON.stringify({
        ProjectCodes: projectCodes,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error("Update ProjectCodes Error:", errorText);
      throw new Error(`Update fehlgeschlagen (${response.status}): ${errorText}`);
    }

    console.log(`ProjectCodes updated for ${partitionKey}/${rowKey}`);
    return true;
  } catch (error) {
    console.error("Update ProjectCodes Error:", error);
    throw error;
  }
};

// Update ImageProjectCode for an image (individual image codes)
export const updateImageProjectCode = async (partitionKey, rowKey, imageProjectCode) => {
  try {
    validateConfig();

    const url = `https://${AZURE_CONFIG.storageAccount}.table.core.windows.net/${AZURE_CONFIG.tableName}(PartitionKey='${encodeURIComponent(partitionKey)}',RowKey='${encodeURIComponent(rowKey)}')?${AZURE_CONFIG.sasToken}`;

    const response = await fetch(url, {
      method: "MERGE",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json;odata=nometadata",
        "If-Match": "*",
      },
      body: JSON.stringify({
        ImageProjectCode: imageProjectCode,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error("Update ImageProjectCode Error:", errorText);
      throw new Error(`Update fehlgeschlagen (${response.status}): ${errorText}`);
    }

    console.log(`ImageProjectCode updated for ${partitionKey}/${rowKey}`);
    return true;
  } catch (error) {
    console.error("Update ImageProjectCode Error:", error);
    throw error;
  }
};

// Update ImageDescription for an image
export const updateImageDescription = async (partitionKey, rowKey, imageDescription) => {
  try {
    validateConfig();

    const url = `https://${AZURE_CONFIG.storageAccount}.table.core.windows.net/${AZURE_CONFIG.tableName}(PartitionKey='${encodeURIComponent(partitionKey)}',RowKey='${encodeURIComponent(rowKey)}')?${AZURE_CONFIG.sasToken}`;

    const response = await fetch(url, {
      method: "MERGE",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json;odata=nometadata",
        "If-Match": "*",
      },
      body: JSON.stringify({
        ImageDescription: imageDescription,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error("Update ImageDescription Error:", errorText);
      throw new Error(`Update fehlgeschlagen (${response.status}): ${errorText}`);
    }

    console.log(`ImageDescription updated for ${partitionKey}/${rowKey}`);
    return true;
  } catch (error) {
    console.error("Update ImageDescription Error:", error);
    throw error;
  }
};

// Update NextcloudPath for an image (after Nextcloud upload)
export const updateNextcloudPath = async (partitionKey, rowKey, nextcloudPath) => {
  try {
    validateConfig();

    const url = `https://${AZURE_CONFIG.storageAccount}.table.core.windows.net/${AZURE_CONFIG.tableName}(PartitionKey='${encodeURIComponent(partitionKey)}',RowKey='${encodeURIComponent(rowKey)}')?${AZURE_CONFIG.sasToken}`;

    const response = await fetch(url, {
      method: "MERGE",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json;odata=nometadata",
        "If-Match": "*",
      },
      body: JSON.stringify({
        NextcloudPath: nextcloudPath,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error("Update NextcloudPath Error:", errorText);
      throw new Error(`Update fehlgeschlagen (${response.status}): ${errorText}`);
    }

    console.log(`NextcloudPath updated for ${partitionKey}/${rowKey}`);
    return true;
  } catch (error) {
    console.error("Update NextcloudPath Error:", error);
    throw error;
  }
};

// Delete a single reference image
export const deleteReferenceImage = async (partitionKey, rowKey) => {
  try {
    validateConfig();

    const url = `https://${AZURE_CONFIG.storageAccount}.table.core.windows.net/${AZURE_CONFIG.tableName}(PartitionKey='${encodeURIComponent(partitionKey)}',RowKey='${encodeURIComponent(rowKey)}')?${AZURE_CONFIG.sasToken}`;

    const response = await fetch(url, {
      method: "DELETE",
      headers: {
        Accept: "application/json;odata=nometadata",
        "If-Match": "*",
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error("Delete Image Error:", errorText);
      throw new Error(`Löschen fehlgeschlagen (${response.status}): ${errorText}`);
    }

    console.log(`Image deleted: ${partitionKey}/${rowKey}`);
    return true;
  } catch (error) {
    console.error("Delete Image Error:", error);
    throw error;
  }
};

// Delete multiple reference images (for deleting entire collections)
export const deleteMultipleReferenceImages = async (images) => {
  const results = [];
  const errors = [];

  for (const image of images) {
    try {
      await deleteReferenceImage(image.partitionKey, image.rowKey);
      results.push({ success: true, image });
    } catch (error) {
      errors.push({ success: false, image, error: error.message });
    }
  }

  if (errors.length > 0) {
    console.error(`${errors.length} of ${images.length} images failed to delete`);
  }

  return { results, errors, totalDeleted: results.length, totalFailed: errors.length };
};
